import { useState } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { toast } from 'sonner';
import { MapPin, Phone, Mail, Clock, Send, Shield, Facebook, Twitter, Instagram, Building2, Users, FileText, Briefcase } from 'lucide-react';

// Embassy contact departments - keys for translation
const DEPARTMENT_KEYS = [
  { nameKey: 'contact.dept_ambassador', descKey: 'contact.dept_ambassador_desc', icon: Building2, email: 'gabinete@embajadangola.es' },
  { nameKey: 'contact.dept_consular', descKey: 'contact.dept_consular_desc', icon: FileText, email: 'consular@embajadangola.es' },
  { nameKey: 'contact.dept_communication', descKey: 'contact.dept_communication_desc', icon: Users, email: 'comunicacao@embajadangola.es' },
  { nameKey: 'contact.dept_economic', descKey: 'contact.dept_economic_desc', icon: Briefcase, email: 'comercial@embajadangola.es' },
  { nameKey: 'contact.dept_admin', descKey: 'contact.dept_admin_desc', icon: Building2, email: 'admin@embajadangola.es' },
];

export default function Contact() {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    await new Promise(r => setTimeout(r, 1000));
    toast.success(t('contact.message_sent'));
    setForm({ name: '', email: '', subject: '', message: '' });
    setSending(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-10">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">{t('contact.title')}</h1>
          <p className="text-gray-500 text-sm md:text-base">{t('contact.subtitle')}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Info */}
          <div className="lg:col-span-1 space-y-5">
            {/* Emergency */}
            <div className="bg-[#CC0000] text-white rounded-xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <Shield size={18} className="text-[#FFD700]" />
                <h3 className="font-bold">{t('contact.emergency')}</h3>
              </div>
              <a href="tel:+34914356430" className="text-2xl font-bold block mb-1">+34 914 35 64 30</a>
              <p className="text-red-100 text-xs">{t('contact.emergency_desc')}</p>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
              <div className="flex gap-3">
                <div className="w-9 h-9 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
                  <MapPin size={16} className="text-[#CC0000]" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{t('contact.address')}</p>
                  <p className="text-sm text-gray-700">Calle Lagasca, 88, 2º Andar<br />Salamanca, 28001 Madrid, España</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-9 h-9 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
                  <Phone size={16} className="text-[#CC0000]" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{t('contact.phone')}</p>
                  <a href="tel:+34914356430" className="text-sm text-gray-700 hover:text-[#CC0000]">+34 914 35 64 30</a>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-9 h-9 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
                  <Mail size={16} className="text-[#CC0000]" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{t('contact.email')}</p>
                  <a href="mailto:embaixada.espanha@mirex.gov.ao" className="text-sm text-gray-700 hover:text-[#CC0000]">embaixada.espanha@mirex.gov.ao</a>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-9 h-9 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
                  <Clock size={16} className="text-[#CC0000]" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{t('contact.hours')}</p>
                  <p className="text-sm text-gray-700 font-medium">{t('contact.weekdays_hours')}</p>
                  <p className="text-xs text-gray-400">{t('contact.weekends_closed')}</p>
                </div>
              </div>
            </div>

            {/* Social Media */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">{t('contact.social_media')}</p>
              <div className="flex gap-3">
                {[{ icon: Facebook, label: 'Facebook' }, { icon: Twitter, label: 'Twitter' }, { icon: Instagram, label: 'Instagram' }].map(({ icon: Icon, label }) => (
                  <a key={label} href="#" aria-label={label}
                    className="w-9 h-9 rounded-full bg-gray-100 hover:bg-[#CC0000] hover:text-white flex items-center justify-center text-gray-500 transition-colors">
                    <Icon size={16} />
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Main Content: Form -> Map -> Departments */}
          <div className="lg:col-span-2 space-y-6">
            {/* Contact Form - FIRST */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-5">{t('contact.send_message')}</h2>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{t('contact.name')}</label>
                    <input type="text" value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))}
                      required placeholder={t('contact.your_name')} data-testid="contact-name"
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{t('contact.email')}</label>
                    <input type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))}
                      required placeholder={t('contact.your_email')} data-testid="contact-email"
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('contact.subject')}</label>
                  <input type="text" value={form.subject} onChange={e => setForm(f => ({...f, subject: e.target.value}))}
                    required placeholder={t('contact.subject_placeholder')} data-testid="contact-subject"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('contact.message')}</label>
                  <textarea value={form.message} onChange={e => setForm(f => ({...f, message: e.target.value}))}
                    required placeholder={t('contact.message_placeholder')} rows={5} data-testid="contact-message"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none resize-none" />
                </div>
                <button type="submit" disabled={sending} data-testid="contact-submit"
                  className="flex items-center gap-2 px-6 py-2.5 bg-[#CC0000] text-white font-semibold rounded-md hover:bg-[#A30000] disabled:opacity-60 transition-colors text-sm">
                  <Send size={14} /> {sending ? t('common.sending') : t('contact.send_message')}
                </button>
              </form>
            </div>

            {/* Map - SECOND */}
            <div className="bg-gray-200 rounded-xl h-64 overflow-hidden relative">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3037.5!2d-3.6857!3d40.4261!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xd422890c6b1b2a3%3A0x5b8f3e3f3e3f3e3f!2sCalle%20de%20Lagasca%2C%2088%2C%2028001%20Madrid!5e0!3m2!1spt!2ses!4v1"
                className="w-full h-full border-0"
                allowFullScreen loading="lazy"
                title="Localização da Embaixada de Angola em Madrid"
              />
            </div>

            {/* Department Emails - THIRD */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-5">{t('contact.departments')}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {DEPARTMENT_KEYS.map(dept => {
                  const Icon = dept.icon;
                  return (
                    <div key={dept.nameKey} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                      <div className="w-9 h-9 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
                        <Icon size={16} className="text-[#CC0000]" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900">{t(dept.nameKey)}</p>
                        <p className="text-xs text-gray-500 mb-1">{t(dept.descKey)}</p>
                        <a href={`mailto:${dept.email}`} className="text-xs text-[#CC0000] hover:underline font-medium">
                          {dept.email}
                        </a>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
